import {
  DateField,
  FileField,
  HiddenField,
  MarkdownField,
  TextField,
} from "@components/fields";
import { FlashMessage } from "@components/flash_messages/flash_message";
import { useMutate } from "@hooks/use_mutate";
import { ApiModelError } from "@root/types";
import { api } from "@services/api";
import { appRoutes } from "@services/app_routes";
import { isNil, prepareFormikInitialValues } from "@services/typed_helpers";
import { Form, Formik } from "formik";
import React, { type FC, useCallback, useMemo } from "react";

import * as types from "../../../types";

interface PressItemFormProps {
  pressItem: types.PressItemModel;
}

const transformToFormInputs = (
  serverData: types.PressItemModel,
): types.PressItemInputValues => {
  const { id, ...data } = serverData;

  const inputData = {
    ...data,
    id: id?.toString(),
    image: undefined,
  };

  return inputData;
};

const PressItemForm: FC<PressItemFormProps> = ({ pressItem }) => {
  // It's unclear where we would put an image so for now,
  // we're going to leave this field out.  Since the plumbing
  // is already put together, we'll simply turn off the field for now.
  const includeImageField = false;

  const updatePressItem = useCallback(
    (token: string) => (pressItemData?: types.PressItemPostValues) => {
      if (isNil(pressItemData)) {
        throw "Studio event data must be present!";
      }
      if (pressItemData.id) {
        return api.pressItems.update(token, pressItemData);
      } else {
        return api.pressItems.create(token, pressItemData!);
      }
    },
    [],
  );

  const initialValues = useMemo(
    () =>
      prepareFormikInitialValues<
        types.PressItemInputValues,
        types.PressItemInputValues
      >(transformToFormInputs(pressItem)),
    [pressItem],
  );

  const {
    submitData: submit,
    loading,
    error: apiError,
    data,
  } = useMutate<types.PressItemUpdateResponse, types.PressItemPostValues>(
    updatePressItem,
  );
  const { errors } = data ?? {};
  const handleSubmit = (values: types.PressItemInputValues) => {
    if (loading) {
      return;
    }
    submit(values as types.PressItemPostValues).then((data) => {
      if (data?.success) {
        const currentUrl = new URL(window.location.href);
        currentUrl.pathname = appRoutes.admin.pressItems.index();

        window.location.replace(currentUrl);
      }
    });
  };

  const formatErrors = (errors: ApiModelError): string[] => {
    return Object.entries(errors).flatMap(([fieldName, errorWords]) => {
      return errorWords.map(
        (error) =>
          `${fieldName[0].toUpperCase() + fieldName.slice(1)} ${error}`,
      );
    });
  };
  return (
    <div className="press-item-form">
      {errors ? (
        <FlashMessage message={formatErrors(errors)} type="error" />
      ) : null}
      {apiError ? (
        <FlashMessage
          message="Something terrible has happened.  Please try again later."
          type="error"
        />
      ) : null}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        id="press_item_form"
      >
        <Form encType="multipart/form-data" method="post">
          <HiddenField id="pressItemId" name="id" />
          <TextField
            name="tagline"
            label="Tagline"
            helpElement="Keep it short! e.g. KRON TV Live about Art"
          />
          <TextField
            name="headline"
            label="Headline"
            helpElement="e.g. 1890 Opens It's doors for the weekend"
          />
          <MarkdownField name="description" label="Description" />
          <DateField
            name="publishedDate"
            label="Published date"
            helpElement="This date is the day the press was published."
          />
          <TextField
            name="source"
            label="Source"
            helpElement="e.g. Mission Local"
          />
          <TextField
            name="link"
            label="Link"
            helpElement="e.g. https://missionlocal.org/series/1890-bryant-street-studios-spring-art-open-studios/"
          />
          {includeImageField ? (
            <FileField
              name="image"
              label="Image"
              disabled
              helpElement="This image will be shown on with the press item.  Ideally a portrait 4x5 aspect ratio."
              accept="image/*"
            />
          ) : null}
          <div className="admin-form-controls">
            <div className="admin-form-controls--button">
              <a role="button" href="/admin/press" className="secondary">
                Cancel
              </a>
            </div>
            <div className="admin-form-controls--button">
              <input type="submit" value={pressItem.id ? "Update" : "Create"} />
            </div>
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export { PressItemForm };
